import { useEffect, useRef, useState } from "react"
import type { SerializedDockview } from "dockview"
import { OK, ERROR } from "helpers/data-statuses"
import { useDashboardInstance } from "resources/dashboard-instances"
import { useLayout } from "resources/layouts"
import { useAppDispatch } from "hooks/redux"
import { useIsRealInstance } from "./use-is-real-instance"
import { readLayoutConfig } from "../contracts/layouts"
import { initFromLayoutConfig } from "../layouts/init-from-layout-config"
import { hydrateWorkspace } from "../layouts/hydrate-workspace"

export interface InstanceWorkspaceLoad {
  // Gate the consumer tree on this so the sync hooks mount only AFTER the seed/hydrate dispatch —
  // render 0 must not flash defaults, and the sync hooks must see the seeded isPreferencesLoaded.
  isInitialized: boolean
  // True only when the instance GET succeeded. Gates the writer: on ERROR the slices hold client
  // defaults (no seed), so persisting would clobber the server workspace we couldn't load.
  loadSucceeded: boolean
  // Seeded arrangement for the layout engine (workspace.arrangement wins, else layout config's).
  instanceArrangement: SerializedDockview | null
  // Drift inputs: the live layout version vs the version this instance was seeded from.
  layoutVersion: number | null
  layoutVersionSeeded: number | null
}

const FLAG_OFF: InstanceWorkspaceLoad = {
  isInitialized: true,
  loadSucceeded: false,
  instanceArrangement: null,
  layoutVersion: null,
  layoutVersionSeeded: null,
}

function asArrangement(blob: Record<string, unknown> | undefined): SerializedDockview | null {
  // Trust cast (ADR 0003): the arrangement is a verbatim Dockview toJSON blob the server validated
  // on write; the client loads it as-is into the layout engine without re-validating its shape.
  return blob ? (blob as unknown as SerializedDockview) : null
}

/**
 * Seed-from-config on instance open (APP-2705). Loads the instance (with its workspace snapshot)
 * and its layout, then seeds the slices ONCE per instance:
 *
 *   1. Seed the layout config as the baseline (initFromLayoutConfig).
 *   2. If the instance has a saved workspace, hydrate it ON TOP — the workspace WINS.
 *
 * The seed runs in an effect and flips `isInitialized` only after dispatching, so the caller can
 * gate the sync-hook subtree on it: the sync hooks then mount with the slices already seeded
 * (isPreferencesLoaded true), treat them as authoritative, and never clobber them with a stale
 * per-subtype read. This is the seed-before-sync ordering the plan calls the crux.
 *
 * Resource gates accept OK || ERROR: on ERROR we initialize WITHOUT seeding (the slices fall back
 * to client defaults via the sync hooks) rather than hanging on a spinner.
 *
 * No-op when the flag is off (returns isInitialized:true immediately — today's path is untouched).
 */
export function useInstanceWorkspaceLoader(
  dashboardId: string,
): InstanceWorkspaceLoad {
  // A real instance id is always a UUID. The "default" sentinel and unmigrated slug dashboards 404,
  // so don't fetch/seed them — they render client-side until APP-3155 migrates them.
  const isRealInstance = useIsRealInstance(dashboardId)
  const dispatch = useAppDispatch()

  const [instance, instanceStatus] = useDashboardInstance(isRealInstance ? dashboardId : null)
  const layoutId = instance?.layoutId ?? null
  const [layout, layoutStatus] = useLayout(isRealInstance && layoutId ? layoutId : null)

  const [isInitialized, setIsInitialized] = useState(false)
  const [instanceArrangement, setInstanceArrangement] = useState<SerializedDockview | null>(null)

  // Run-once latch scoped to the instance identity (not a boolean) so a different instance still
  // seeds; the component is remounted per dashboardId, but this is belt-and-suspenders.
  const seededForRef = useRef<string | null>(null)

  const instanceSettled = instanceStatus === OK || instanceStatus === ERROR
  const layoutSettled = !layoutId || layoutStatus === OK || layoutStatus === ERROR

  useEffect(() => {
    if (!isRealInstance) return
    if (seededForRef.current === dashboardId) return
    if (!instanceSettled || !layoutSettled) return

    // Seed the layout baseline first (when present + readable), then let the saved workspace win.
    let arrangement: SerializedDockview | null = null
    if (layout && layoutStatus === OK) {
      const result = readLayoutConfig(layout.config)
      if (result.status === "ok") {
        initFromLayoutConfig(dispatch, dashboardId, result.config)
        arrangement = asArrangement(result.config.arrangement)
      }
    }

    const workspace = instance?.workspace
    if (workspace) {
      hydrateWorkspace(dispatch, dashboardId, workspace)
      // The instance's own arrangement wins over the layout's when present.
      arrangement = asArrangement(workspace.arrangement) ?? arrangement
    }

    seededForRef.current = dashboardId
    setInstanceArrangement(arrangement)
    setIsInitialized(true)
  }, [isRealInstance, dashboardId, instanceSettled, layoutSettled, instance, layout, layoutStatus, dispatch])

  // Flag off, or the "default" sentinel (no server instance): initialize immediately with no seed —
  // the default dashboard renders client-side, exactly today's path.
  if (!isRealInstance) {
    return FLAG_OFF
  }

  return {
    isInitialized,
    loadSucceeded: instanceStatus === OK,
    instanceArrangement,
    layoutVersion: layout?.version ?? null,
    layoutVersionSeeded: instance?.layoutVersionSeeded ?? null,
  }
}
